/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/StartTimer.ts
 * @create:      2025-07-01 18:01:58.214
 * @modify:      2025-07-11 21:19:12.954
 * @version:     6.0.0
 * @times:       5
 * @lines:       61
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Timer } from "../class/Timer";
import { TimeSpan } from "../class/TimeSpan";
import { ITimeSpan } from "../interface/ITimeSpan";
import { ToMilliseconds } from "../number/ToMilliseconds";
import { TTimeUnit } from "../type/advanced";

/**
 * Starts a timer that executes the provided callback function at specified intervals.
 * 
 * @param callback - The function to be executed when the timer elapses. It can be either
 *                   a synchronous function that returns `void` or an asynchronous function
 *                   that returns a `Promise<void>`.
 * @param interval - The interval at which the timer should fire. It can be either a number
 *                   representing the time duration or an `ITimeSpan` object.
 * @param unit - The time unit of the `interval` if it's a number. Defaults to "milliseconds".
 *               This parameter is optional and is used only when `interval` is a number.
 * @returns An instance of the `Timer` class that has been started and is set to execute
 *          the callback at the specified interval.
 * 
 * @since 4.3.0
 * @version 2023-11-27
 */
export function StartTimer(callback: () => void | Promise<void>, interval: number | ITimeSpan, unit?: TTimeUnit) {
    const timer = new Timer(new TimeSpan(typeof interval === "number" ? ToMilliseconds(interval, unit ?? "milliseconds") : ToMilliseconds(interval)));
    timer.Elapsed.on(callback);
    timer.Start();
    return timer;
}
